import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, GripVertical, AlertTriangle, Minus, ChefHat, Users2 } from "lucide-react";
import { CATEGORIES, UNITS, SECTIONS, CUISINES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const emptyIng = () => ({
  original_name: "", normalized_name: "", quantity: "", unit: "g",
  raw_quantity: "", category: "epicerie", source_section: "ingredients",
  confidence: 1, optional: false,
});
const emptyStep = (n) => ({ number: n, title: "", instructions: "", confidence: 1 });

export default function RecipeForm({ initial, onSave, saving, submitLabel = "Enregistrer" }) {
  const { user } = useAuth();
  const [title, setTitle] = useState(initial?.title || "");
  const [shared, setShared] = useState(initial?.visibility ? initial.visibility === "shared" : true);
  const [aiTitle, setAiTitle] = useState(initial?.title_is_ai_generated || false);
  const [servings, setServings] = useState(initial?.servings || 2);
  const [prep, setPrep] = useState(initial?.prep_time_minutes ?? "");
  const [cook, setCook] = useState(initial?.cook_time_minutes ?? "");
  const [ingredients, setIngredients] = useState(
    (initial?.ingredients?.length ? initial.ingredients : [emptyIng()]).map((i) => ({
      ...i, quantity: i.quantity === null || i.quantity === undefined ? "" : i.quantity,
    }))
  );
  const [steps, setSteps] = useState(
    initial?.steps?.length ? initial.steps : [emptyStep(1)]
  );
  const [utensils, setUtensils] = useState((initial?.utensils || []).join(", "));
  const [allergens, setAllergens] = useState((initial?.allergens || []).join(", "));
  const [nut, setNut] = useState(initial?.nutrition || {});
  const [cuisine, setCuisine] = useState(initial?.cuisine || "autre");
  const [images] = useState(initial?.images || []);

  const setIng = (idx, field, val) =>
    setIngredients((arr) => arr.map((it, i) => (i === idx ? { ...it, [field]: val } : it)));

  const setStep = (idx, field, val) =>
    setSteps((arr) => arr.map((it, i) => (i === idx ? { ...it, [field]: val } : it)));

  const removeIng = (idx) => setIngredients((arr) => arr.filter((_, i) => i !== idx));
  const removeStep = (idx) =>
    setSteps((arr) => arr.filter((_, i) => i !== idx).map((s, i) => ({ ...s, number: i + 1 })));

  const handleSubmit = () => {
    const payload = {
      title: title.trim() || "Nouvelle recette",
      title_is_ai_generated: aiTitle,
      servings: Number(servings) || 2,
      prep_time_minutes: prep === "" ? null : Number(prep),
      cook_time_minutes: cook === "" ? null : Number(cook),
      ingredients: ingredients
        .filter((i) => (i.original_name || i.normalized_name).trim())
        .map((i) => ({
          original_name: i.original_name || i.normalized_name,
          normalized_name: i.normalized_name || i.original_name,
          quantity: i.quantity === "" || i.quantity === null ? null : Number(i.quantity),
          unit: i.unit || "",
          raw_quantity: i.raw_quantity || `${i.quantity ?? ""} ${i.unit || ""}`.trim(),
          category: i.category || "autre",
          source_section: i.source_section || "ingredients",
          confidence: i.confidence ?? 1,
          optional: !!i.optional,
        })),
      steps: steps
        .filter((s) => s.instructions.trim() || s.title.trim())
        .map((s, i) => ({ number: i + 1, title: s.title, instructions: s.instructions, confidence: s.confidence ?? 1 })),
      utensils: utensils.split(",").map((s) => s.trim()).filter(Boolean),
      allergens: allergens.split(",").map((s) => s.trim()).filter(Boolean),
      nutrition: {
        calories_kcal: nut.calories_kcal ?? null,
        energy_kj: nut.energy_kj ?? null,
        fat_g: nut.fat_g ?? null,
        carbohydrates_g: nut.carbohydrates_g ?? null,
        protein_g: nut.protein_g ?? null,
      },
      images,
      is_favorite: initial?.is_favorite || false,
      source: initial?.source || "manual",
      cuisine,
      shared: user?.can_share ? shared : false,
    };
    onSave(payload);
  };

  const lowConf = (c) => c !== undefined && c < 0.7;

  return (
    <div className="space-y-6" data-testid="recipe-form">
      {/* Partage (utilisateurs autorisés uniquement) */}
      {user?.can_share && (
        <div className="flex items-center justify-between rounded-2xl border border-black/5 bg-white p-3 at-shadow" data-testid="share-toggle-row">
          <span className="flex items-center gap-2 text-sm font-medium">
            <Users2 size={18} style={{ color: "var(--at-sage)" }} /> Partager avec toute la famille
          </span>
          <Switch checked={shared} onCheckedChange={setShared} data-testid="share-toggle" />
        </div>
      )}
      {/* Titre */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>
            Titre de la recette
          </Label>
          {aiTitle && (
            <Badge className="border-0" style={{ background: "var(--at-honey-light)", color: "#92400E" }}>
              <AlertTriangle size={12} className="mr-1" /> Titre proposé par l'IA
            </Badge>
          )}
        </div>
        <Input
          data-testid="recipe-title-input"
          value={title}
          onChange={(e) => { setTitle(e.target.value); setAiTitle(false); }}
          placeholder="Ex : Nouilles au poulet sauce cacahuète"
          className="rounded-2xl border-black/10 bg-white text-lg font-medium"
        />
      </div>

      {/* Portions + temps */}
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>Portions</Label>
          <div className="flex items-center gap-1 rounded-2xl border border-black/10 bg-white p-1">
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8 rounded-xl" data-testid="servings-minus"
              onClick={() => setServings((s) => Math.max(1, Number(s) - 1))}><Minus size={16} /></Button>
            <span className="flex-1 text-center font-semibold" data-testid="servings-value">{servings}</span>
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8 rounded-xl" data-testid="servings-plus"
              onClick={() => setServings((s) => Number(s) + 1)}><Plus size={16} /></Button>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>Prépa (min)</Label>
          <Input data-testid="prep-input" type="number" value={prep} onChange={(e) => setPrep(e.target.value)}
            className="rounded-2xl border-black/10 bg-white" placeholder="—" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>Cuisson (min)</Label>
          <Input data-testid="cook-input" type="number" value={cook} onChange={(e) => setCook(e.target.value)}
            className="rounded-2xl border-black/10 bg-white" placeholder="—" />
        </div>
      </div>

      {/* Type de cuisine */}
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>Type de cuisine</Label>
        <Select value={cuisine} onValueChange={setCuisine}>
          <SelectTrigger className="rounded-2xl border-black/10 bg-white" data-testid="cuisine-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CUISINES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Ingrédients */}
      <div className="space-y-3">
        <h3 className="font-display text-xl font-semibold">Ingrédients</h3>
        {ingredients.map((ing, idx) => (
          <div
            key={idx}
            className={`rounded-2xl border bg-white p-3 at-shadow ${lowConf(ing.confidence) ? "border-amber-300" : "border-black/5"}`}
            data-testid={`ingredient-row-${idx}`}
          >
            <div className="mb-2 flex items-center gap-2">
              <Input
                data-testid={`ingredient-name-${idx}`}
                value={ing.normalized_name || ing.original_name}
                onChange={(e) => { setIng(idx, "normalized_name", e.target.value); setIng(idx, "original_name", e.target.value); }}
                placeholder="Nom de l'ingrédient"
                className="flex-1 rounded-xl border-black/10 font-medium"
              />
              <Button type="button" size="icon" variant="ghost" className="h-9 w-9 shrink-0 rounded-xl text-red-500"
                data-testid={`ingredient-remove-${idx}`} onClick={() => removeIng(idx)}><Trash2 size={16} /></Button>
            </div>
            {lowConf(ing.confidence) && (
              <Badge className="mb-2 border-0" style={{ background: "#FEF3C7", color: "#92400E" }}>
                <AlertTriangle size={12} className="mr-1" /> À vérifier
              </Badge>
            )}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Input data-testid={`ingredient-qty-${idx}`} type="number" step="any" value={ing.quantity}
                onChange={(e) => setIng(idx, "quantity", e.target.value)} placeholder="Qté"
                className="rounded-xl border-black/10" />
              <Select value={ing.unit || ""} onValueChange={(v) => setIng(idx, "unit", v)}>
                <SelectTrigger className="rounded-xl border-black/10" data-testid={`ingredient-unit-${idx}`}><SelectValue placeholder="Unité" /></SelectTrigger>
                <SelectContent>
                  {UNITS.map((u) => <SelectItem key={u || "none"} value={u || "none"}>{u || "aucune"}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={ing.category} onValueChange={(v) => setIng(idx, "category", v)}>
                <SelectTrigger className="rounded-xl border-black/10" data-testid={`ingredient-cat-${idx}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.icon} {c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={ing.source_section} onValueChange={(v) => setIng(idx, "source_section", v)}>
                <SelectTrigger className="rounded-xl border-black/10" data-testid={`ingredient-section-${idx}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SECTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" className="w-full rounded-2xl border-dashed" data-testid="add-ingredient-btn"
          onClick={() => setIngredients((a) => [...a, emptyIng()])}>
          <Plus size={16} className="mr-1" /> Ajouter un ingrédient
        </Button>
      </div>

      {/* Étapes */}
      <div className="space-y-3">
        <h3 className="font-display text-xl font-semibold">Étapes</h3>
        {steps.map((step, idx) => (
          <div key={idx} className="rounded-2xl border border-black/5 bg-white p-3 at-shadow" data-testid={`step-row-${idx}`}>
            <div className="mb-2 flex items-center gap-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
                style={{ background: "var(--at-brand)" }}>{idx + 1}</span>
              <Input data-testid={`step-title-${idx}`} value={step.title}
                onChange={(e) => setStep(idx, "title", e.target.value)} placeholder="Titre de l'étape (optionnel)"
                className="flex-1 rounded-xl border-black/10 font-medium" />
              <Button type="button" size="icon" variant="ghost" className="h-9 w-9 shrink-0 rounded-xl text-red-500"
                data-testid={`step-remove-${idx}`} onClick={() => removeStep(idx)}><Trash2 size={16} /></Button>
            </div>
            <Textarea data-testid={`step-text-${idx}`} value={step.instructions}
              onChange={(e) => setStep(idx, "instructions", e.target.value)} placeholder="Instructions..."
              className="min-h-[70px] rounded-xl border-black/10" />
          </div>
        ))}
        <Button type="button" variant="outline" className="w-full rounded-2xl border-dashed" data-testid="add-step-btn"
          onClick={() => setSteps((a) => [...a, emptyStep(a.length + 1)])}>
          <Plus size={16} className="mr-1" /> Ajouter une étape
        </Button>
      </div>

      {/* Extras */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>Ustensiles (séparés par ,)</Label>
          <Input data-testid="utensils-input" value={utensils} onChange={(e) => setUtensils(e.target.value)}
            className="rounded-2xl border-black/10 bg-white" placeholder="Casserole, Poêle..." />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>Allergènes (séparés par ,)</Label>
          <Input data-testid="allergens-input" value={allergens} onChange={(e) => setAllergens(e.target.value)}
            className="rounded-2xl border-black/10 bg-white" placeholder="Gluten, Soja..." />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>Valeurs nutritionnelles (par portion)</Label>
        <div className="grid grid-cols-5 gap-2">
          {[["calories_kcal", "kcal"], ["energy_kj", "kJ"], ["protein_g", "Prot."], ["carbohydrates_g", "Gluc."], ["fat_g", "Lip."]].map(([k, lab]) => (
            <Input key={k} data-testid={`nut-${k}`} type="number" step="any" value={nut[k] ?? ""}
              onChange={(e) => setNut((n) => ({ ...n, [k]: e.target.value === "" ? null : Number(e.target.value) }))}
              placeholder={lab} className="rounded-xl border-black/10 bg-white" />
          ))}
        </div>
      </div>

      <Button
        data-testid="save-recipe-btn"
        onClick={handleSubmit}
        disabled={saving}
        className="h-14 w-full rounded-2xl text-base font-semibold text-white at-shadow-lg at-press"
        style={{ background: "var(--at-brand)" }}
      >
        <ChefHat size={20} className="mr-2" /> {saving ? "Enregistrement..." : submitLabel}
      </Button>
    </div>
  );
}
